// Composer — submit player actions/speech, pick which character you act as,
// begin/continue the session, and (host-only) send freeform DM directions or undo.
import { useEffect, useRef, useState } from "react";
import type { AuthUser, FullGame, WsClientMessage } from "../lib/types";

// Owner-level control mirrors the server's _game_owner_ok: global admins always;
// a game with no recorded owner (legacy / solo / anonymous) stays open to anyone;
// otherwise only the recorded owner. The full-game WS payload carries owner_id
// even though the FullGame type doesn't declare it, so read it defensively.
function isHost(game: FullGame, you: AuthUser | null): boolean {
  if (you?.is_admin) return true;
  const ownerId = (game as { owner_id?: string | null }).owner_id;
  if (!ownerId) return true;
  return !!you && you.id === ownerId;
}

const COACH_COMPOSER_KEY = "hearth.coach.composer.v1";
const COACH_TURN_KEY = "hearth.coach.turn.v1";

// A few plain-language example actions to seed a newcomer's first turn.
const EXAMPLE_ACTIONS = [
  "I look around the room for anything out of place.",
  "I walk up to the stranger and ask what they want.",
  "I ready my weapon and stay alert for danger.",
];

export function Composer({
  game,
  hasStarted,
  busy,
  send,
  you,
  inCombat = false,
  spotlightMine = null,
  spotlightDim = false,
}: {
  game: FullGame;
  hasStarted: boolean;
  busy: boolean;
  send: (msg: WsClientMessage) => boolean;
  you: AuthUser | null;
  /** Live combat flag (from Play's snapshot). Enables the one-time turn-primer card. */
  inCombat?: boolean;
  /** Conductor baton: when the DM spotlights a character THIS client controls, its id —
   *  the composer emphasizes/focuses itself and pre-selects that hero. null otherwise. */
  spotlightMine?: string | null;
  /** True when the DM is spotlighting ANOTHER human: the table stays open (composer usable)
   *  but visually de-emphasized so their moment reads clearly. */
  spotlightDim?: boolean;
}) {
  const humanSeats = game.seats.filter((s) => s.kind === "human" && s.character_id);
  // Default to the character on YOUR seat, else the first human seat.
  const defaultCharId = () =>
    humanSeats.find((s) => you && s.user_id === you.id)?.character_id
    || humanSeats[0]?.character_id
    || "";
  const [asId, setAsId] = useState<string>(defaultCharId());
  const [text, setText] = useState("");
  const [kind, setKind] = useState<"action" | "speech">("action");
  const [dmText, setDmText] = useState("");
  const [showDm, setShowDm] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const [confirmBegin, setConfirmBegin] = useState(false);

  const host = isHost(game, you);

  // First-visit coaching. Seeded from localStorage so it shows once per browser.
  const [showComposerCoach, setShowComposerCoach] = useState(false);
  const [showTurnCoach, setShowTurnCoach] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(COACH_COMPOSER_KEY)) setShowComposerCoach(true);
    } catch { /* private mode / no storage — just skip coaching */ }
  }, []);
  // The turn primer fires the first time this browser sees combat begin.
  useEffect(() => {
    if (!inCombat) return;
    try {
      if (!localStorage.getItem(COACH_TURN_KEY)) setShowTurnCoach(true);
    } catch { /* ignore */ }
  }, [inCombat]);

  function dismissComposerCoach() {
    setShowComposerCoach(false);
    try { localStorage.setItem(COACH_COMPOSER_KEY, "1"); } catch { /* ignore */ }
  }
  function dismissTurnCoach() {
    setShowTurnCoach(false);
    try { localStorage.setItem(COACH_TURN_KEY, "1"); } catch { /* ignore */ }
  }

  // Close the overflow menu on outside click / Escape.
  const overflowRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Conductor baton lands on one of ours: pull focus to the composer and pre-select the
  // spotlighted hero so the invited player can just start typing.
  useEffect(() => {
    if (!spotlightMine) return;
    if (humanSeats.some((s) => s.character_id === spotlightMine)) setAsId(spotlightMine);
    try { textareaRef.current?.focus(); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotlightMine]);
  useEffect(() => {
    if (!showOverflow) return;
    const onDoc = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) setShowOverflow(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setShowOverflow(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [showOverflow]);

  // Reconcile the selection whenever seats change: if the chosen character is no
  // longer a seated human PC (late claim, seat cleared, seats prop arriving after
  // mount), fall back to your seat / the first option.
  useEffect(() => {
    if (!humanSeats.some((s) => s.character_id === asId)) setAsId(defaultCharId());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.seats, you]);

  function submit() {
    const t = text.trim();
    if (!t) return;
    if (!asId) return;
    if (send({ action: "player_action", character_id: asId, text: t, kind })) {
      setText("");
      if (showComposerCoach) dismissComposerCoach();
    }
  }

  function fillExample(example: string) {
    setKind("action");
    setText(example);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  // ── pre-session: only the host starts the table ──
  if (!hasStarted) {
    const total = game.seats.length;
    const filled = game.seats.filter((s) => s.character_id).length;
    const allSeated = filled >= total;
    const readiness = `${filled} of ${total} ${total === 1 ? "seat" : "seats"} filled`;

    if (!host) {
      return (
        <div className="composer">
          <div className="composer__inner begin-banner">
            <div className="hint" style={{ fontSize: 15, textAlign: "center" }}>
              Waiting for the host to begin the session…
            </div>
            <div className="hint">{readiness}</div>
          </div>
        </div>
      );
    }

    function doBegin() {
      if (!allSeated && !confirmBegin) { setConfirmBegin(true); return; }
      send({ action: "begin" });
    }

    return (
      <div className="composer">
        <div className="composer__inner begin-banner">
          <button className="btn btn--primary btn--lg" disabled={busy || filled === 0}
            onClick={doBegin}>
            {confirmBegin && !allSeated ? "Begin anyway?" : "✦ Begin the session"}
          </button>
          <div className="hint">
            {filled === 0
              ? "Seat at least one character to begin."
              : confirmBegin && !allSeated
              ? `${readiness} — start with an incomplete party?`
              : `${readiness}. The Dungeon Master will set the opening scene.`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="composer">
      <div className="composer__inner">
        {showTurnCoach && (
          <div className="coach-mark coach-mark--turn card" role="note"
            style={{ position: "relative", padding: 14, marginBottom: 10 }}>
            <button className="icon-btn" aria-label="Dismiss" onClick={dismissTurnCoach}
              style={{ position: "absolute", top: 8, right: 8 }}>✕</button>
            <div style={{ fontFamily: "var(--serif)", fontSize: 16, marginBottom: 8, paddingRight: 24 }}>
              A fight has begun — how a turn works
            </div>
            <ul className="muted" style={{ margin: "0 0 10px", paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>
              <li>On your turn you get a <b>move</b>, one <b>action</b> (attack, cast a spell, or do something clever), and often a <b>bonus action</b>.</li>
              <li>Use the action bar for attacks &amp; spells, or just <b>describe what you do</b> below — the DM resolves the dice.</li>
              <li>When you're done, end your turn and the DM plays the foes.</li>
            </ul>
            <button className="btn btn--sm" onClick={dismissTurnCoach}>Got it</button>
          </div>
        )}

        {showComposerCoach && humanSeats.length > 0 && (
          <div className="coach-mark card" role="note"
            style={{ position: "relative", padding: 14, marginBottom: 10 }}>
            <button className="icon-btn" aria-label="Dismiss" onClick={dismissComposerCoach}
              style={{ position: "absolute", top: 8, right: 8 }}>✕</button>
            <div style={{ fontSize: 14, marginBottom: 10, paddingRight: 24 }}>
              Describe what you <b>do</b> or <b>say</b> in plain words — the DM handles the rules and dice.
            </div>
            <div className="flex" style={{ flexWrap: "wrap", gap: 6 }}>
              {EXAMPLE_ACTIONS.map((ex) => (
                <button key={ex} type="button" className="btn btn--ghost btn--sm"
                  style={{ fontWeight: 400, whiteSpace: "normal", textAlign: "left" }}
                  onClick={() => fillExample(ex)}>
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="composer__controls">
          {humanSeats.length > 0 ? (
            <div className="as-select">
              <span>Acting as</span>
              <select className="field" style={{ width: "auto", padding: "5px 8px" }}
                value={asId} onChange={(e) => setAsId(e.target.value)}>
                {humanSeats.map((s) => (
                  <option key={s.character_id!} value={s.character_id!}>
                    {s.character?.name || s.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <span className="muted" style={{ fontSize: 13 }}>Spectating — all seats are AI</span>
          )}
          <div className="kind-toggle">
            <button className={kind === "action" ? "on" : ""} onClick={() => setKind("action")}>▸ Action</button>
            <button className={kind === "speech" ? "on" : ""} onClick={() => setKind("speech")}>“ Speak</button>
          </div>
          <div className="grow" />
          <button className="btn btn--ghost btn--sm" onClick={() => send({ action: "continue" })} disabled={busy}>
            Continue
          </button>
          {host && (
            <div className="composer__overflow" ref={overflowRef} style={{ position: "relative" }}>
              <button className="btn btn--ghost btn--sm" aria-haspopup="menu" aria-expanded={showOverflow}
                title="Host controls" onClick={() => setShowOverflow((v) => !v)}>
                ⋯ <span style={{ fontSize: 10, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.4 }}>host</span>
              </button>
              {showOverflow && (
                <div className="composer__menu card" role="menu"
                  style={{ position: "absolute", right: 0, bottom: "calc(100% + 6px)", zIndex: 40,
                    minWidth: 190, padding: 6, display: "flex", flexDirection: "column", gap: 2 }}>
                  <button className="btn btn--ghost btn--sm" role="menuitem" disabled={busy}
                    onClick={() => { send({ action: "undo" }); setShowOverflow(false); }}
                    title="Take back the last action (misclick safety)">↶ Undo last action</button>
                  <button className="btn btn--ghost btn--sm" role="menuitem"
                    onClick={() => { setShowDm((v) => !v); setShowOverflow(false); }}>
                    🎙 Direct the DM…
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {host && showDm && (
          <div className="dm-direct-row">
            <input className="field" value={dmText} onChange={(e) => setDmText(e.target.value)}
              autoFocus
              placeholder="Instruct the DM directly (e.g. 'skip ahead to the next morning')…"
              onKeyDown={(e) => {
                if (e.key === "Enter" && dmText.trim()) {
                  send({ action: "dm_direct", text: dmText.trim() }); setDmText("");
                }
              }} />
            <button className="btn btn--sm" disabled={!dmText.trim()}
              onClick={() => { send({ action: "dm_direct", text: dmText.trim() }); setDmText(""); }}>Send</button>
          </div>
        )}

        <div className="composer__input-row" style={spotlightDim ? { opacity: 0.72 } : undefined}>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            disabled={humanSeats.length === 0}
            style={spotlightMine ? { boxShadow: "0 0 0 2px var(--gold)", borderColor: "var(--gold)" } : undefined}
            placeholder={
              humanSeats.length === 0
                ? "All seats are AI — use Continue to let the table play on."
                : kind === "speech"
                ? "What do you say?"
                : "What do you do?"
            }
          />
          <button className="btn btn--primary" disabled={busy || !text.trim() || !asId} onClick={submit}
            title={!asId ? "Claim a seat with a character to act" : busy ? "The table is thinking…" : "Send"}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
